import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
  /** Render-prop receives the generated id + aria props to spread on the control. */
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
  labelHidden?: boolean;
}

export function Field({ label, hint, error, required, className, children, labelHidden }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <label htmlFor={id} className={cn('text-xs font-semibold text-ink-2', labelHidden && 'sr-only')}>
        {label}
        {required && (
          <span className="ml-0.5 text-suspicious" aria-hidden>
            *
          </span>
        )}
      </label>
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy })}
      {hint && !error && (
        <p id={hintId} className="text-xs text-ink-3">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} role="alert" className="text-xs font-medium text-suspicious">
          {error}
        </p>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input(
  { className, ...rest },
  ref,
) {
  return <input ref={ref} className={cn('ctl', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, rows = 3, ...rest },
  ref,
) {
  return <textarea ref={ref} rows={rows} className={cn('ctl', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select(
  { className, children, ...rest },
  ref,
) {
  return (
    <select ref={ref} className={cn('ctl', className)} {...rest}>
      {children}
    </select>
  );
});

export const Checkbox = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label?: ReactNode }>(
  function Checkbox({ className, label, id, ...rest }, ref) {
    const auto = useId();
    const cid = id ?? auto;
    const box = (
      <input
        ref={ref}
        id={cid}
        type="checkbox"
        className={cn('size-4 shrink-0 cursor-pointer rounded-[3px] accent-[var(--accent)]', className)}
        {...rest}
      />
    );
    if (!label) return box;
    return (
      <label htmlFor={cid} className="inline-flex cursor-pointer items-center gap-2 text-sm">
        {box}
        <span>{label}</span>
      </label>
    );
  },
);

export function FormError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div role="alert" className="rounded-[var(--radius-ctl)] border border-suspicious/40 bg-suspicious-soft px-3 py-2 text-sm text-suspicious">
      {message}
    </div>
  );
}
